/*
Item: Form for users to edit their own data
Name: UserData
Format: Frontend component, partial screen
*/

import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Messages } from 'primereact/messages';
import MessageService from '../service/MessageService';
import DataForm from '../components/DataForm';
import updateTemplate from '../service/data/TemplateUpdateDTO.json';
import JsonHelper, { StatisticsAccessArray, RoleAccessHelper, StatisticsAccessHelper } from '../utils/statisticsUtils';
import accessProfileTemplate from '../service/data/TemplateAccessProfileDTO.json';
import { formTitles, formTypes } from '../utils/userFormTypes';
import { lockStates } from '../utils/lockStates';

function handleKeyDown(event) {
  if (event.keyCode === 13) {
    event.preventDefault(); // todo check if this should be used
  }
}

function getYesterday() {
  const dateDt = new Date();
  dateDt.setDate(dateDt.getDate() - 1);
  const dd = String(dateDt.getDate()).padStart(2, '0');
  const mm = String(dateDt.getMonth() + 1).padStart(2, '0'); // January is 0!
  const yyyy = dateDt.getFullYear();
  return `${dd}.${mm}.${yyyy} 00:00:00`;
}

// class which offers the possibility to edit userData or to add a new user
// this class contains all functionality - the components are rendered in DataForm file
export default class UserData extends Component {
  constructor(props) {
    super(props);
    const { formType, location } = this.props;
    this.formType = formType;
    this.id = -1; // id of the loaded user (in BE is tested if id and username fit together)

    let initialUsername; // username of the user whose data is modified
    try {
      if (this.formType === formTypes.adminCreatesNewUser) {
        initialUsername = 'tempUserName';
      } else if (this.formType === formTypes.userEditsItself) {
        initialUsername = sessionStorage.getItem('username');
      } else if (this.formType === formTypes.adminEditsUser) {
        initialUsername = location.state.entry.name;
      } else {
        initialUsername = undefined;
      }
    } catch (err) {
      // nothing
    }
    this.username = initialUsername;

    this.state = {
      // state for DataForm
      lastName: '',
      firstName: '',
      phone: '',
      fax: '',
      email: '',
      institution: '',
      institution2: '',
      street: '',
      houseNumber: '',
      zipcode: '',
      city: '',
      // state for DataForm only necessary for new user
      role: '',
      // state for DataForm only necessary for edit user by admin
      generateNewPassword: false,
      lockState: lockStates.loading,
      // state for save dialog
      progressDialogVisible: false,
      saveDialogVisible: false,
      abortDialogVisible: false,
      // state for access panel
      accessprofile: '', // todo why is this needed?
      accessprofileID: -1, // todo why is this needed?
      accessProfileReference: null, // todo why is this needed?
      accesses: [],
      accessesEntries: [],
    };
    this.showMessage = this.showMessage.bind(this);
    this.wrapMessage = this.wrapMessage.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.openSaveDialog = this.openSaveDialog.bind(this);
    this.handleSaveYes = this.handleSaveYes.bind(this);
    this.handleSaveNo = this.handleSaveNo.bind(this);
    this.openAbortDialog = this.openAbortDialog.bind(this);
    this.handleAbortYes = this.handleAbortYes.bind(this);
    this.handleAbortNo = this.handleAbortNo.bind(this);
    this.handleChange = this.handleChange.bind(this);

    this.handleSelectAllAccessRights = this.handleSelectAllAccessRights.bind(this);
    this.handleDeselectAllAccessRights = this.handleDeselectAllAccessRights.bind(this);
    this.onAccessChange = this.onAccessChange.bind(this);
    this.toggleNewPassword = this.toggleNewPassword.bind(this);
    this.toggleLock = this.toggleLock.bind(this);

    this.messageService = new MessageService();
    const { updateBreadcrumbNames } = this.props;
    this.updateBreadcrumbNames = updateBreadcrumbNames;
  }

  // onLoad read current data
  componentDidMount = async () => {
    if (this.formType === formTypes.adminCreatesNewUser) {
      this.group = sessionStorage.getItem('group');
      this.groupID = sessionStorage.getItem('groupID');
    } else {
      const doUpdateOwnData = (this.formType === formTypes.userEditsItself);
      const userData = await this.messageService.postUserProperties(
        this.username,
        doUpdateOwnData,
      );
      // console.log('group#',this.formType, userData.group, userData.groupID);
      this.id = userData.id;
      this.group = userData.group;
      this.groupID = userData.groupID;
      const stateUpdate = {
        firstName: userData.contact.firstName,
        lastName: userData.contact.lastName,
        role: userData.role,
        phone: userData.contact.phone[0],
        fax: userData.contact.fax[0],
        email: userData.contact.email[0] || '',
        institution: userData.company.companyName,
        institution2: userData.contact.organisationUnit,
        street: userData.contact.street,
        houseNumber: userData.contact.houseNumber,
        zipcode: userData.contact.postID,
        city: userData.contact.city,
        accessprofile: userData.accessprofile,
        accessprofileID: userData.accessprofileID,
        accessProfileReference: userData.accessProfileReference,
        accesses: userData.accessProfileReference
          ? userData.accessProfileReference.map((entry) => entry.name) : [],
        accessesEntries: userData.accessProfileReference
          ? userData.accessProfileReference.map(
            (entry) => ({ value: entry.name, id: entry.id }),
          ) : [],
        lockState: userData.validTo ? lockStates.locked : lockStates.open,
      };
      this.setState(stateUpdate);
    }
  };

  handleChange = (e) => {
    if (e.target.name === 'role' && e.target.value !== 'INTERNAL_USER') {
      this.handleSelectAllAccessRights();
    }
    this.setState({
      [e.target.name]: e.target.value,
    });
  };

  // handler for dialog abort No-button, hides dialogue
  handleAbortNo() {
    this.setState({ abortDialogVisible: false });
  }

  // handler for dialog abort Yes-button, hides dialogue and calls abort handler
  handleAbortYes() {
    this.setState({ abortDialogVisible: false }, () => {
      const { history } = this.props;
      if (this.formType === formTypes.adminCreatesNewUser) {
        history.push('../useradmin');
      } else {
        history.push('./');
      }
    });
  }

  handleSaveYes() {
    this.setState({ saveDialogVisible: false });
    if (this.validateForm()) {
      return this.wrapMessage();
    }
    return Promise.resolve();
  }

  handleDeselectAllAccessRights() {
    this.setState({ accesses: [], accessesEntries: [] });
  }

  handleSelectAllAccessRights() {
    this.setState({
      accesses: StatisticsAccessArray.map((entry) => entry.value),
      accessesEntries: StatisticsAccessArray,
    });
  }

  handleSaveNo() {
    this.setState({ saveDialogVisible: false });
  }

  onAccessChange(e) {
    const { accesses } = this.state;
    const selectedAccesses = [...accesses];
    if (e.checked) {
      selectedAccesses.push(e.value);
    } else {
      selectedAccesses.splice(selectedAccesses.indexOf(e.value), 1);
    }
    this.setState(
      {
        accesses: selectedAccesses,
        accessesEntries: selectedAccesses.map(
          (entry) => ({ value: entry, id: StatisticsAccessHelper[entry] }),
        ),
      },
    );
  }

  wrapMessage = async () => {
    const {
      lastName, firstName, accessprofile, accessprofileID, accessProfileReference, phone,
      email, fax, institution, institution2, city, street, houseNumber, zipcode, role,
      accessesEntries, generateNewPassword, lockState,
    } = this.state;
    const updateMessage = JsonHelper.deepCopy(updateTemplate);
    updateMessage.contents[
      'genesis.container.content.GenesisTMContent'
    ].uuid = sessionStorage.getItem('uuid');
    const { transfermodel } = updateMessage.contents[
      'genesis.container.content.GenesisTMContent'
    ];
    transfermodel.name = this.username;
    transfermodel.role = role;
    transfermodel.group = this.group;
    transfermodel.groupID = this.groupID;
    transfermodel.accessProfileReference = accessProfileReference;
    transfermodel.fullName = `${firstName} ${lastName}`;
    transfermodel.contact.lastName = lastName;
    transfermodel.contact.firstName = firstName;
    transfermodel.contact.phone[0] = phone;
    transfermodel.contact.fax[0] = fax;
    transfermodel.contact.email[0] = email;
    transfermodel.company.companyName = institution;
    transfermodel.contact.organisationUnit = institution2;
    transfermodel.contact.street = street;
    transfermodel.contact.houseNumber = houseNumber;
    transfermodel.contact.postID = zipcode;
    transfermodel.contact.city = city;
    transfermodel.id = this.id;
    transfermodel.active = 'true'; // ticket 555
    const roleTemplate = JsonHelper.deepCopy(accessProfileTemplate);
    // INTERNAL_USER oder GROUP_ADMIN
    roleTemplate.name = RoleAccessHelper[role];// 'FachNutzer' oder 'FachAdministrator'
    roleTemplate.id = '-1';
    /**
    /* add specific info to the updateMessage depending on formType
    * */
    // formType admin creates New User
    if (this.formType === formTypes.adminCreatesNewUser) {
      transfermodel.passwd = 'Initial001';
      transfermodel.startDir = [
        'genesis.util.types.OnlineStartingPageType',
        'UNDEF',
      ];
      transfermodel.accessProfileReference = accessesEntries.map(
        (entry) => {
          const accessTemplate = JsonHelper.deepCopy(accessProfileTemplate);
          accessTemplate.name = entry.value;
          accessTemplate.id = '-1'; // was entry.id
          return accessTemplate;
        },
      );
      // eslint-disable-next-line brace-style
    } // formType admin edits user
    else if (this.formType === formTypes.adminEditsUser) {
      updateMessage.contents[
        'genesis.container.content.GenesisTMContent'
      ].parameter.hashMap.selektion[0] = this.username;
      if (generateNewPassword) {
        updateMessage.contents[
          'genesis.container.content.GenesisTMContent'
        ].parameter.hashMap.mailversand = ['true'];

        updateMessage.contents[
          'genesis.container.content.GenesisTMContent'
        ].dispatchData = {
          function: 16,
          supplement: -1, // supplement describes that action is performed by admin
          event: 0,
          objectType: 'USER',
        };
      }
      transfermodel.accessprofile = accessprofile;
      transfermodel.accessprofileID = accessprofileID;
      transfermodel.accessProfileReference = accessesEntries.map(
        (entry) => {
          const accessTemplate = JsonHelper.deepCopy(accessProfileTemplate);
          accessTemplate.name = entry.value;
          accessTemplate.id = '-1';
          return accessTemplate;
        },
      );
      if (lockState === lockStates.willChangeToLocked) {
        transfermodel.validTo = getYesterday();
        // todo - check if it is necessary to log out the user in this case
      } else if (lockState === lockStates.willChangeToOpen) {
        transfermodel.validTo = null;
      }
      if (generateNewPassword) {
        const userData = await this.messageService.postPasswordResetFromAdmin();
        transfermodel.passwd = userData.passwd;
      }
      // eslint-disable-next-line brace-style
    } // formType user edits itself.
    else if (this.formType === formTypes.userEditsItself) {
      transfermodel.accessprofile = accessprofile;
      transfermodel.accessprofileID = accessprofileID;
    }
    // push roleTemplate in transfermodel.accessProfileReference
    transfermodel.accessProfileReference.push(roleTemplate);
    /**
     /* start the request and invoke additional functions depending on formType
     * */
    let textMessage;
    if (this.formType === formTypes.userEditsItself || this.formType === formTypes.adminEditsUser) {
      const doUpdateOwnData = (this.username === sessionStorage.getItem('username'));
      this.showProgressDialog();
      try {
        textMessage = await this.messageService.postUserUpdate(
          updateMessage,
          doUpdateOwnData,
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('ERROR in messageService.postUserUpdate', err);
      } finally {
        this.hideProgressDialog();
      }
      // update hover display on name
      if (doUpdateOwnData) {
        sessionStorage.setItem('lastName', lastName);
        sessionStorage.setItem('firstName', firstName);
        sessionStorage.setItem('institution', institution);
        this.updateBreadcrumbNames(firstName, lastName);
      }

      this.resetLockState();
    } else if (this.formType === formTypes.adminCreatesNewUser) {
      this.showProgressDialog();
      try {
        textMessage = await this.messageService.postUserRegistrate(updateMessage);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('ERROR in messageService.postUserRegistrate', err);
      } finally {
        this.hideProgressDialog();
      }

      this.clearAllFields();
      this.handleDeselectAllAccessRights();
    } else {
      // eslint-disable-next-line no-console
      console.error('not implemented feature!');
    }
    this.showMessage(textMessage[0], textMessage[1]);
  }

  toggleLock() {
    const { lockState } = this.state;
    switch (lockState) {
      case lockStates.open:
        this.setState({ lockState: lockStates.willChangeToLocked });
        break;
      case lockStates.locked:
        this.setState({ lockState: lockStates.willChangeToOpen });
        break;
      case lockStates.willChangeToLocked:
        this.setState({ lockState: lockStates.open });
        break;
      case lockStates.willChangeToOpen:
        this.setState({ lockState: lockStates.locked });
        break;
      default:
    }
  }

  toggleNewPassword() {
    const { generateNewPassword } = this.state;
    this.setState({ generateNewPassword: !generateNewPassword });
  }

  openSaveDialog(event) {
    event.preventDefault();
    this.setState({ saveDialogVisible: true });
  }

  showProgressDialog() {
    this.setState({ progressDialogVisible: true });
  }

  hideProgressDialog() {
    this.setState({ progressDialogVisible: false });
  }

  resetLockState() {
    const { lockState } = this.state;
    if (lockState === lockStates.willChangeToOpen) {
      this.setState({ lockState: lockStates.open });
    }
    if (lockState === lockStates.willChangeToLocked) {
      this.setState({ lockState: lockStates.locked });
    }
  }

  openAbortDialog(event) {
    event.preventDefault();
    this.setState({ abortDialogVisible: true });
  }

  showMessage(messageDetail, messageSeverity) {
    const msg = {
      life: 5000,
      severity: messageSeverity,
      detail: messageDetail,
    };
    this.messages.show(msg);
  }

  validateForm() {
    const {
      email, lastName, firstName, institution, role,
    } = this.state;
    const emailValid = email.match(
      /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i,
    );
    if (this.formType === formTypes.userEditsItself) {
      return (emailValid && lastName && firstName && institution);
    } if (this.formType === formTypes.adminCreatesNewUser) {
      return (emailValid && lastName && firstName && institution && this.username && role);
    }
    if (this.formType === formTypes.adminEditsUser) {
      return (emailValid && lastName && firstName && institution && this.username && role);
    }
    return false;
  }

  clearAllFields() {
    this.setState({ role: '' });
    this.setState({ lastName: '' });
    this.setState({ firstName: '' });
    this.setState({ email: '' });
    this.setState({ phone: '' });
    this.setState({ fax: '' });
    this.setState({ institution: '' });
    this.setState({ institution2: '' });
    this.setState({ street: '' });
    this.setState({ houseNumber: '' });
    this.setState({ zipcode: '' });
    this.setState({ city: '' });
  }

  render() {
    const {
      lastName, firstName, accesses, phone,
      email, fax, institution, institution2, city,
      street, houseNumber, zipcode, progressDialogVisible, saveDialogVisible,
      abortDialogVisible, role, generateNewPassword, lockState,
    } = this.state;
    const noSelfEdit = !(this.formType === 'userEditsItself');
    if (noSelfEdit) {
      const isAdmin = sessionStorage.getItem('role') === 'GROUP_ADMIN';
      const isSysAdmin = sessionStorage.getItem('role') === 'SYSTEM_ADMIN';
      // const isStBA = sessionStorage.getItem('group') === 'StBA';
      if (!(isSysAdmin || isAdmin)) {
        return null;
      }
    }
    return (
      <DataForm
        formType={this.formType}
        formTitle={formTitles[this.formType]}
        username={this.username}
        lastName={lastName}
        firstName={firstName}
        phone={phone}
        fax={fax}
        email={email}
        institution={institution}
        institution2={institution2}
        street={street}
        houseNumber={houseNumber}
        zipcode={zipcode}
        city={city}
        role={role}
        progressDialogVisible={progressDialogVisible}
        saveDialogVisible={saveDialogVisible}
        abortDialogVisible={abortDialogVisible}
        accesses={accesses}
        generateNewPassword={generateNewPassword}
        lockState={lockState}
        handleKeyDown={handleKeyDown}
        handleChange={this.handleChange}
        validateForm={this.validateForm}
        openAbortDialog={this.openAbortDialog}
        handleAbortYes={this.handleAbortYes}
        handleAbortNo={this.handleAbortNo}
        openSaveDialog={this.openSaveDialog}
        handleSaveYes={this.handleSaveYes}
        handleSaveNo={this.handleSaveNo}
        toggleNewPassword={this.toggleNewPassword}
        toggleLock={this.toggleLock}
        handleSelectAll={this.handleSelectAllAccessRights}
        handleDeselectAll={this.handleDeselectAllAccessRights}
        onAccessChange={this.onAccessChange}
      >
        <Messages
          ref={(el) => { (this.messages = el); }}
          style={{ marginBottom: '1em' }}
        />
      </DataForm>
    );
  }
}

UserData.propTypes = {
  updateBreadcrumbNames: PropTypes.func.isRequired,
  history: PropTypes.instanceOf(Object),
  formType: PropTypes.string.isRequired,
  location: PropTypes.instanceOf(Object),
};

UserData.defaultProps = {
  history: null,
  location: null,
};
